import { Component, OnInit } from "@angular/core";
import { SafeHtml } from "@angular/platform-browser";
import { getApp } from "../app";
import { getDashboardData } from "../api.service";
import { NON_OM_CLIENT, RM_CLIENT } from "../customers/customers.service";
import { Subscription } from "rxjs";
import {
  DashboardChartData,
  DashboardModel,
} from "../../../../shared/models/dashboard";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit {
  chartsList: string[] = [];
  rmClient: boolean = false;
  nonOMClient: boolean = false;
  htmlContent: SafeHtml = "";
  time: number = 0;

  private langChangeSubscription: Subscription = new Subscription();
  private filterChangeSubscription: Subscription = new Subscription();

  app = getApp((app) => {
    this.app = app;
  });

  constructor() {}

  async ngOnInit() {
    this.chartsList = this.app.list.graphs.charts;
    this.rmClient = this.app.customers.getRMCustomers() === RM_CLIENT;
    this.nonOMClient = this.app.customers.getOMCustomers() === NON_OM_CLIENT;
    this.time = Date.now();

    await this.loadDashboardsPage();

    this.langChangeSubscription = this.app.footer.onLangChange.subscribe(
      async (value) => {
        if (value) {
          await this.loadDashboardsPage();
        }
      }
    );
    this.filterChangeSubscription =
      this.app.filterDashboard.onFilterChange.subscribe(async (value) => {
        if (value) {
          await this.loadDashboardsPage();
        }
      });
  }

  async loadDashboardsPage() {
    await this.getDashboardData();

    setTimeout(async () => {
      await this.loadSingleLineScripts();
      this.loadTextScripts();
    }, 10);
  }

  getOptionsForStatusChart() {
    /** added exception for statuses, as we don't need the status unconfirmed and ingored */
    /**https://app.asana.com/0/1209495870609871/1209855413803933 */

    let options = [];
    options = this.app.field.getOptionsAndTextsForASpecificField(
      this.app.fieldId.part.obsolescenceStatus
    );

    let unconfirmedIndex = options.findIndex(
      (item) => item.value === "unconfirmed"
    );
    if (unconfirmedIndex > -1) {
      options.splice(unconfirmedIndex, 1);
    }
    let ignoredIndex = options.findIndex((item) => item.value === "resolved");
    if (ignoredIndex > -1) {
      options.splice(ignoredIndex, 1);
    }
    return options;
  }

  async getDashboardData() {
    try {
      // Build charts based on client chartsList
      const charts: Record<string, DashboardChartData> = {};
      const allCharts: Record<string, () => DashboardChartData> =
        this.getChartsSetup();
      let excludedCharts: string[] = [];

      if (this.app.state.isAnalyst || this.app.state.isRMAdmin) {
        excludedCharts = this.app.list.graphs.omCharts;
      }

      this.chartsList.forEach((chartKey) => {
        if (allCharts[chartKey] && !excludedCharts.includes(chartKey)) {
          (charts as any)[chartKey] = allCharts[chartKey]();
        }
      });

      // Build dashboardModel
      const dashboardModel: DashboardModel = {
        charts: charts,
        overview: {
          filters: this.app.filterDashboard.filters,
          labels: {
            total: this.app.text.graphs.total,
            totalUniqueCases: this.app.text.graphs.totalUniqueCases,
            currency: this.app.text.graphs.currency,
            currencySize: this.app.text.graphs.currencySize,
          },
        },
      };

      // Get data from the dashboard service
      let response = await getDashboardData(
        this.app.customers.expectCurrent,
        dashboardModel
      );

      // Replace text with the appropriate translation
      this.htmlContent = response
        .replace(/{{statusByMPNTitle}}/g, this.app.text.graphs.statusByMPNTitle)
        .replace(/{{statusByCPNTitle}}/g, this.app.text.graphs.statusByCPNTitle)
        .replace(/{{reachByMPNTitle}}/g, this.app.text.graphs.reachByMPNTitle)
        .replace(/{{reachByCPNTitle}}/g, this.app.text.graphs.reachByCPNTitle)
        .replace(/{{statusByOMFTitle}}/g, this.app.text.graphs.statusByOMFTitle)
        .replace(
          /{{statusOverviewTitle}}/g,
          this.app.text.graphs.statusByMonthTitle
        )
        .replace(
          /{{statusByMonthSubtitle}}/g,
          this.app.text.graphs.statusByMonthSubtitle
        )
        .replace(/{{omApproachTitle}}/g, this.app.text.graphs.omApproachTitle)
        .replace(
          /{{selectedSolutionsTitle}}/g,
          this.app.text.graphs.selectedSolutionsTitle
        )
        .replace(
          /{{costPerFinalSolTitle}}/g,
          this.app.text.graphs.costPerFinalSolTitle
        )
        .replace(/{{contractTitle}}/g, this.app.text.graphs.contractTitle)
        .replace(
          /{{dinCodeRespDepTitle}}/g,
          this.app.text.graphs.dinCodeRespDepTitle
        )
        .replace(
          /{{licenseWarningText}}/g,
          this.app.text.graphs.licenseWarningText
        )
        .replace(
          /{{noDataWarningText}}/g,
          this.app.text.graphs.noDataWarningText
        );
    } catch (error) {
      console.error("Error while loading dashboards page:", error);
    }
  }

  getChartsSetup() {
    return {
      // RM Charts
      mpnStatusChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.manufacturer.obsolescenceStatus
        ),
        hasLicense: this.rmClient,
      }),
      cpnStatusChart: () => ({
        data: this.getOptionsForStatusChart(),
        hasLicense: this.rmClient,
      }),
      mpnReachChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.manufacturer.reachAffected
        ),
        hasLicense: this.rmClient,
      }),
      cpnReachChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.manufacturer.reachAffected
        ),
        hasLicense: this.rmClient,
      }),
      // OM Charts
      omfStatusChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.omfStatus
        ),
        hasLicense: !this.nonOMClient,
      }),
      openAndClosedChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.graphs.abbreviatedMonths
        ),
        hasLicense: !this.nonOMClient,
        optionalInfo: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.graphs.closedNew
        ),
      }),
      omApproachChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.omApproach
        ),
        hasLicense: !this.nonOMClient,
      }),
      selectedSolutionsChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.post.resolveClass
        ),
        hasLicense: !this.nonOMClient,
      }),
      costPerFinalSolutionChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.post.resolveClass
        ),
        hasLicense: !this.nonOMClient,
      }),
      contractChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.contract
        ),
        hasLicense: !this.nonOMClient,
      }),
      dinCodeRespDepChart: () => ({
        data: this.app.field.getOptionsAndTextsForASpecificField(
          this.app.fieldId.thread.dinCodeRespDep
        ),
        hasLicense: !this.nonOMClient,
      }),
    };
  }

  async loadSingleLineScripts() {
    const scripts = document.querySelectorAll("script");
    const scriptLoadPromises: Promise<void>[] = [];

    scripts.forEach((oldScript) => {
      if (oldScript.src) {
        if (document.querySelector(`script[src="${oldScript.src}"]`)) {
          const newScript = document.createElement("script");
          newScript.src = oldScript.src;
          newScript.async = false;

          const scriptPromise = new Promise<void>((resolve) => {
            newScript.onload = () => {
              resolve();
            };
          });

          scriptLoadPromises.push(scriptPromise);
          document.body.appendChild(newScript);
        }
      }
    });

    // Wait for all external scripts to load
    await Promise.all(scriptLoadPromises);
  }

  loadTextScripts() {
    const container = document.querySelector(".dashboard-container");

    if (container) {
      // Remove previously injected scripts & clear previous content
      document.querySelectorAll("script").forEach((script) => script.remove());
      container.innerHTML = "";
      container.innerHTML = this.htmlContent as string;

      const scripts = container.querySelectorAll("script");

      scripts.forEach((oldScript) => {
        if (!oldScript.src && oldScript.textContent) {
          try {
            const func = new Function(oldScript.textContent);
            func();
          } catch (e) {
            console.error("Error executing script:", e);
          }
        }
      });
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    if (this.filterChangeSubscription) {
      this.filterChangeSubscription.unsubscribe();
    }

    this.app.filterDashboard.isOpen = false;
    this.app.filterDashboard.resetFilter(false);
  }
}
